import React, { FormEvent, useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { deleteAppUpdate, fetchAppUpdates, upsertAppUpdate } from "../../store/slices/appUpdateSlice";
import { AppPlatform, AppUpdateRelease, UpdatePolicy } from "../../types/admin";
import { formatDate } from "../../utils/admin";

type PlatformFilter = "all" | AppPlatform;

const platformLabelMap: Record<AppPlatform, string> = {
  ios: "iOS",
  android: "Android",
};

const policyLabelMap: Record<UpdatePolicy, string> = {
  forced: "강제",
  recommended: "추천",
};

const emptyForm = {
  platform: "ios" as AppPlatform,
  version: "",
  updatePolicy: "recommended" as UpdatePolicy,
  isEnabled: true,
  storeUrl: "",
  title: "",
  messageForced: "",
  messageRecommended: "",
};

const VERSION_PATTERN = /^\d+(\.\d+){1,3}$/;
const STORE_URL_PATTERN = /^https?:\/\/.+/i;

const toNullable = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const isValidVersion = (value: string) => VERSION_PATTERN.test(value.trim());

const isValidStoreUrl = (value: string) => {
  const trimmed = value.trim();
  if (!STORE_URL_PATTERN.test(trimmed)) {
    return false;
  }
  try {
    const url = new URL(trimmed);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const AppUpdateSettingsPage = () => {
  const dispatch = useAppDispatch();
  const { items, totalCount, latestByPlatform, loading, error, saving, saveError, deleting, deleteError } =
    useAppSelector((state) => state.appUpdate);

  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("all");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const reload = (filter: PlatformFilter = platformFilter) => {
    dispatch(fetchAppUpdates(filter === "all" ? undefined : { platform: filter }));
  };

  useEffect(() => {
    dispatch(fetchAppUpdates(platformFilter === "all" ? undefined : { platform: platformFilter }));
  }, [dispatch, platformFilter]);

  const resetForm = () => {
    setEditingKey(null);
    setForm(emptyForm);
  };

  const fillForm = (release: AppUpdateRelease) => {
    setEditingKey(`${release.platform}:${release.version}`);
    setForm({
      platform: release.platform,
      version: release.version,
      updatePolicy: release.update_policy,
      isEnabled: release.is_enabled,
      storeUrl: release.store_url || "",
      title: release.title || "",
      messageForced: release.message_forced || "",
      messageRecommended: release.message_recommended || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const version = form.version.trim();
    const storeUrl = form.storeUrl.trim();

    if (!version) {
      window.alert("버전을 입력해 주세요.");
      return;
    }

    if (!isValidVersion(version)) {
      window.alert("버전 형식이 올바르지 않습니다. 예: 1.2.0");
      return;
    }

    if (storeUrl && !isValidStoreUrl(storeUrl)) {
      window.alert("스토어 URL 형식이 올바르지 않습니다. http:// 또는 https://로 시작해 주세요.");
      return;
    }

    try {
      await dispatch(
        upsertAppUpdate({
          platform: form.platform,
          version,
          update_policy: form.updatePolicy,
          is_enabled: form.isEnabled,
          store_url: toNullable(storeUrl),
          title: toNullable(form.title),
          message_forced: toNullable(form.messageForced),
          message_recommended: toNullable(form.messageRecommended),
        })
      ).unwrap();
      window.alert(editingKey ? "버전이 수정되었습니다." : "버전이 등록되었습니다.");
      resetForm();
      reload();
    } catch (err) {
      window.alert((err as string) || "저장에 실패했습니다.");
    }
  };

  const onDelete = async (release: AppUpdateRelease) => {
    if (!window.confirm(`${platformLabelMap[release.platform]} ${release.version} 버전을 삭제할까요?`)) {
      return;
    }

    try {
      await dispatch(deleteAppUpdate({ platform: release.platform, version: release.version })).unwrap();
      window.alert("버전이 삭제되었습니다.");
      if (editingKey === `${release.platform}:${release.version}`) {
        resetForm();
      }
      reload();
    } catch (err) {
      window.alert((err as string) || "삭제에 실패했습니다.");
    }
  };

  const renderLatestCard = (platform: AppPlatform) => {
    const latest = latestByPlatform?.[platform] ?? null;
    return (
      <article className={`app-update-latest-card app-update-latest-card--${platform}`}>
        <div className="app-update-latest-card__head">
          <span className="app-update-platform-badge">{platformLabelMap[platform]}</span>
          <span className="app-update-latest-card__label">현재 기준 버전</span>
        </div>
        {latest ? (
          <>
            <strong className="app-update-latest-card__version">{latest.version}</strong>
            <div className="app-update-latest-card__meta">
              <span className={`app-update-policy-badge app-update-policy-badge--${latest.update_policy}`}>
                {policyLabelMap[latest.update_policy]}
              </span>
              <span className={`app-update-enabled-badge ${latest.is_enabled ? "is-on" : "is-off"}`}>
                {latest.is_enabled ? "사용" : "미사용"}
              </span>
            </div>
          </>
        ) : (
          <p className="app-update-latest-card__empty">등록된 활성 버전 없음</p>
        )}
      </article>
    );
  };

  return (
    <section className="admin-page app-update-page">
      <header className="app-update-page__header">
        <div>
          <h2 className="section-title">앱 업데이트 설정</h2>
          <p className="app-update-page__desc">플랫폼별 버전을 등록하고, 강제/추천 업데이트 정책을 관리합니다.</p>
        </div>
      </header>

      <div className="app-update-latest-grid">
        {renderLatestCard("ios")}
        {renderLatestCard("android")}
      </div>

      <form className="app-update-form" onSubmit={onSubmit}>
        <div className="app-update-form__title-row">
          <h3>{editingKey ? "버전 수정" : "버전 등록"}</h3>
          {editingKey && (
            <button className="table-action-btn" type="button" onClick={resetForm}>
              새로 등록
            </button>
          )}
        </div>

        <div className="app-update-form__grid">
          <label className="app-update-field">
            <span>플랫폼</span>
            <select
              value={form.platform}
              onChange={(event) => setForm((prev) => ({ ...prev, platform: event.target.value as AppPlatform }))}
              disabled={saving || Boolean(editingKey)}
            >
              <option value="ios">iOS</option>
              <option value="android">Android</option>
            </select>
          </label>

          <label className="app-update-field">
            <span>버전</span>
            <input
              placeholder="예: 1.2.0"
              value={form.version}
              onChange={(event) => setForm((prev) => ({ ...prev, version: event.target.value }))}
              disabled={saving || Boolean(editingKey)}
            />
          </label>

          <label className="app-update-field">
            <span>업데이트 정책</span>
            <select
              value={form.updatePolicy}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, updatePolicy: event.target.value as UpdatePolicy }))
              }
              disabled={saving}
            >
              <option value="forced">강제 업데이트</option>
              <option value="recommended">추천 업데이트</option>
            </select>
          </label>

          <label className="app-update-field">
            <span>사용 여부</span>
            <select
              value={form.isEnabled ? "true" : "false"}
              onChange={(event) => setForm((prev) => ({ ...prev, isEnabled: event.target.value === "true" }))}
              disabled={saving}
            >
              <option value="true">사용</option>
              <option value="false">미사용</option>
            </select>
          </label>

          <label className="app-update-field app-update-field--full">
            <span>스토어 URL</span>
            <input
              placeholder="App Store / Play Store URL"
              value={form.storeUrl}
              onChange={(event) => setForm((prev) => ({ ...prev, storeUrl: event.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="app-update-field app-update-field--full">
            <span>모달 제목 (선택)</span>
            <input
              placeholder="비우면 앱 기본 문구 사용"
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="app-update-field">
            <span>강제 업데이트 본문 (선택)</span>
            <textarea
              placeholder="비우면 앱 기본 문구 사용"
              value={form.messageForced}
              onChange={(event) => setForm((prev) => ({ ...prev, messageForced: event.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="app-update-field">
            <span>추천 업데이트 본문 (선택)</span>
            <textarea
              placeholder="비우면 앱 기본 문구 사용"
              value={form.messageRecommended}
              onChange={(event) => setForm((prev) => ({ ...prev, messageRecommended: event.target.value }))}
              disabled={saving}
            />
          </label>
        </div>

        <div className="app-update-form__actions">
          {editingKey && (
            <button className="secondary-btn" type="button" onClick={resetForm} disabled={saving}>
              취소
            </button>
          )}
          <button className="primary-btn" type="submit" disabled={saving}>
            {saving ? "저장 중..." : editingKey ? "수정 저장" : "버전 등록"}
          </button>
        </div>
      </form>

      <div className="app-update-list-panel">
        <div className="app-update-list-panel__toolbar">
          <div className="app-update-filter">
            {(["all", "ios", "android"] as PlatformFilter[]).map((filter) => (
              <button
                key={filter}
                type="button"
                className={`app-update-filter__btn ${platformFilter === filter ? "is-active" : ""}`}
                onClick={() => setPlatformFilter(filter)}
              >
                {filter === "all" ? "전체" : platformLabelMap[filter]}
              </button>
            ))}
          </div>
          <span className="app-update-list-panel__count">총 {totalCount}건</span>
        </div>

        <div className="table-shell table-shell--scroll">
          <table className="admin-table app-update-table">
            <thead>
              <tr>
                <th>플랫폼</th>
                <th>버전</th>
                <th>정책</th>
                <th>사용</th>
                <th>스토어 URL</th>
                <th>수정일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {items.map((release) => {
                const key = `${release.platform}:${release.version}`;
                const isEditing = editingKey === key;
                return (
                  <tr key={release.release_id} className={isEditing ? "is-editing" : undefined}>
                    <td>
                      <span className={`app-update-platform-badge app-update-platform-badge--${release.platform}`}>
                        {platformLabelMap[release.platform]}
                      </span>
                    </td>
                    <td>
                      <strong>{release.version}</strong>
                    </td>
                    <td>
                      <span className={`app-update-policy-badge app-update-policy-badge--${release.update_policy}`}>
                        {policyLabelMap[release.update_policy]}
                      </span>
                    </td>
                    <td>
                      <span className={`app-update-enabled-badge ${release.is_enabled ? "is-on" : "is-off"}`}>
                        {release.is_enabled ? "사용" : "미사용"}
                      </span>
                    </td>
                    <td className="app-update-url-cell">{release.store_url || "-"}</td>
                    <td>{formatDate(release.updated_at)}</td>
                    <td>
                      <div className="table-actions">
                        <button className="table-action-btn" type="button" onClick={() => fillForm(release)}>
                          수정
                        </button>
                        <button
                          className="table-action-btn danger"
                          type="button"
                          disabled={deleting}
                          onClick={() => onDelete(release)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && items.length === 0 && (
                <tr>
                  <td className="empty-row" colSpan={7}>
                    등록된 버전이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {loading && <p className="loading-text">앱 업데이트 목록을 불러오는 중입니다...</p>}
      {error && <p className="error-text">{error}</p>}
      {saveError && <p className="error-text">{saveError}</p>}
      {deleteError && <p className="error-text">{deleteError}</p>}
    </section>
  );
};

export default AppUpdateSettingsPage;
